import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n/config';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, MapPin, Users, XCircle, Loader2, Star } from 'lucide-react';
import { format } from 'date-fns';
import { getBilingualValue } from '@/utils/bilingual-helpers';
import { ChatButton } from '@/components/dashboard/shared/ChatButton';
import { ReviewModal } from './ReviewModal';
import { notify } from '@/utils/notification';
import { supabase } from '@/integrations/supabase/client';
import type { GroupRegistrationItem } from '../hooks/useGroupRegistrations';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface GroupSessionCardProps {
  registration: GroupRegistrationItem;
  showChat?: boolean;
  showReview?: boolean;
  showCancel?: boolean;
  onRefresh?: () => void;
}

export const GroupSessionCard: React.FC<GroupSessionCardProps> = ({
  registration,
  showChat,
  showReview,
  showCancel,
  onRefresh,
}) => {
  const { user } = useAuth();
  const { t } = useTranslation('dashboard');
  const lang = i18n.language;
  const event = registration.event;
  const title = getBilingualValue(lang, event.title_en, event.title_zh, event.title) || 'Untitled';
  const [reviewOpen, setReviewOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const handleCancelRegistration = async () => {
    setCancelling(true);
    try {
      const { data, error } = await supabase.functions.invoke('cancel-group-registration', {
        body: { registrationId: registration.id }
      });
      // Edge function non-2xx returns error with context in data
      if (error) {
        const errMsg = data?.error || error.message || 'Failed to cancel';
        notify.error(t('error', { defaultValue: 'Error' }), errMsg);
        return;
      }
      if (data?.error) {
        notify.error(t('error', { defaultValue: 'Error' }), data.error);
        return;
      }
      notify.success(t('groupSessions.cancelSuccess', { defaultValue: 'Registration cancelled successfully' }));
      onRefresh?.();
    } catch (err: any) {
      console.error('Cancel registration error:', err);
      notify.error(t('error', { defaultValue: 'Error' }), err.message || 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  const getStatusBadge = () => {
    switch (registration.status) {
      case 'registered': return <Badge variant="default">Registered</Badge>;
      case 'attended': return <Badge className="bg-green-100 text-green-800">Attended</Badge>;
      case 'cancelled': return <Badge variant="destructive">Cancelled</Badge>;
      case 'waitlisted': return <Badge variant="secondary">Waitlisted</Badge>;
      default: return <Badge variant="secondary">{registration.status}</Badge>;
    }
  };

  const getPaymentBadge = () => {
    switch (registration.payment_status) {
      case 'paid': case 'captured': return <Badge variant="outline" className="text-green-700 border-green-300">Paid</Badge>;
      case 'hold_placed': return <Badge variant="outline" className="text-amber-700 border-amber-300">Hold</Badge>;
      case 'pending': return <Badge variant="outline" className="text-yellow-700 border-yellow-300">Pending</Badge>;
      case 'refunded': return <Badge variant="outline" className="text-blue-700 border-blue-300">Refunded</Badge>;
      default: return null;
    }
  };

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow">
      {event.cover_image_url && (
        <img src={event.cover_image_url} alt={title} className="w-full h-32 object-cover" />
      )}
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <Badge variant="outline" className="text-xs mb-1">Group Session</Badge>
            <h3 className="font-semibold text-base line-clamp-2">{title}</h3>
          </div>
          {getStatusBadge()}
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4 shrink-0" />
          <span>{format(new Date(event.event_date), 'MMM d, yyyy HH:mm')}</span>
          <span className="text-xs">({event.duration_minutes} min)</span>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4 shrink-0" />
          <span>{event.location_type === 'virtual' ? '🌐' : '📍'} {event.location_details || (event.location_type === 'virtual' ? 'Online' : 'In-person')}</span>
        </div>

        {registration.precise_address && ['registered', 'attended'].includes(registration.status) && (
          <div className="flex items-center gap-2 text-sm text-primary bg-primary/5 rounded px-2 py-1">
            <MapPin className="h-4 w-4 shrink-0" />
            <span className="font-medium">{registration.precise_address}</span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Users className="h-4 w-4 shrink-0" />
          <span>{registration.practitioner_name}</span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {getPaymentBadge()}
          <span className="text-sm font-medium">
            {(registration.price_at_registration ?? event.price) > 0
              ? `${registration.currency_at_registration || event.currency} ${registration.price_at_registration ?? event.price}`
              : t('events.free', { defaultValue: 'Free' })
            }
          </span>
        </div>

        <div className="flex items-center gap-2 pt-1 border-t flex-wrap">
          <div className="text-xs text-muted-foreground flex-1">
            {t('groupSessions.registeredOn', { defaultValue: 'Registered' })}: {format(new Date(registration.registered_at), 'MMM d, yyyy')}
          </div>
          {showChat && (
            <ChatButton
              groupEventId={event.id}
              otherParticipantName={registration.practitioner_name}
              currentUserType="client"
              size="sm"
            />
          )}
          {showCancel && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" className="text-destructive" disabled={cancelling}>
                  <XCircle className="h-4 w-4 mr-1" />
                  <span className="hidden md:inline">{t('groupSessions.cancel', { defaultValue: 'Cancel' })}</span>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('groupSessions.cancelTitle', { defaultValue: 'Cancel Registration?' })}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('groupSessions.cancelDescription', { defaultValue: 'Are you sure you want to cancel your registration? Any applicable refund will be processed automatically.' })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('groupSessions.keepRegistration', { defaultValue: 'Keep Registration' })}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleCancelRegistration} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    {cancelling ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                    {t('groupSessions.confirmCancel', { defaultValue: 'Yes, Cancel' })}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {showReview && user && (
            <>
              <Button variant="outline" size="sm" onClick={() => setReviewOpen(true)}>
                <Star className="h-4 w-4 mr-1" />
                <span className="hidden md:inline">Review</span>
              </Button>
              <ReviewModal
                isOpen={reviewOpen}
                onClose={() => setReviewOpen(false)}
                groupEventId={event.id}
                practitionerId={event.practitioner_id}
                practitionerName={registration.practitioner_name}
                clientId={user.id}
              />
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
